import React, { useCallback, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";
import { useAppDispatch } from "src/store/hooks";
import {
  UseCreditForm,
  useCreditNote as applyCreditNote,
} from "src/store/reducers/creditNotes";
import { BookingData, fetchBookingsMaster } from "src/store/reducers/bookings";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";

type IUseCreditModal = {
  show: boolean;
  hide: () => void;
  creditNoteId: string;
  remainingAmount: number;
};

const UseCreditModal = ({
  show,
  hide,
  creditNoteId,
  remainingAmount,
}: IUseCreditModal) => {
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState(false);
  const [bookingOptions, setBookingOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [bookingSearchStatus, setBookingSearchStatus] = useState<
    "idle" | "pending" | "success"
  >("idle");
  const abortRef = useRef<AbortController>();

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    touched,
    errors,
    setErrors,
    setFieldValue,
  } = useFormik<{
    amount_used: string;
    booking: string;
    notes: string;
  }>({
    initialValues: {
      amount_used: "",
      booking: "",
      notes: "",
    },
    validationSchema: Yup.object({
      amount_used: Yup.number()
        .required("Amount is required")
        .typeError("Amount must be a number")
        .positive("Amount must be positive")
        .max(remainingAmount, `Cannot exceed remaining amount (${remainingAmount})`),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      setPending(true);
      submitUse(vals);
    },
  });

  const submitUse = useCallback(
    async (vals: { amount_used: string; booking: string; notes: string }) => {
      try {
        const payload: UseCreditForm = { amount_used: vals.amount_used };
        if (vals.booking) payload.booking = vals.booking;
        if (vals.notes) payload.notes = vals.notes;

        await dispatch(applyCreditNote({ id: creditNoteId, data: payload })).unwrap();
        toast.success("Credit applied successfully");
        resetForm();
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [creditNoteId, dispatch, hide, resetForm, setErrors]
  );

  const handleBookingSearch = useCallback(
    async (inputValue: string) => {
      if (abortRef.current) abortRef.current.abort();
      if (!inputValue) {
        setBookingOptions([]);
        setBookingSearchStatus("idle");
        return;
      }
      setBookingSearchStatus("pending");
      try {
        abortRef.current = new AbortController();
        const data = await dispatch(
          fetchBookingsMaster({
            filter: { keyword: inputValue },
            page: 1,
            signal: abortRef.current.signal,
          })
        ).unwrap();
        setBookingOptions(
          data.results.map((b: BookingData) => ({
            label: `${b.customer_name} — ${new Date(b.arrival_date).toLocaleDateString("en-IN")} — ₹${b.total_amount}`,
            value: b.id,
          }))
        );
        setBookingSearchStatus("success");
      } catch (err: any) {
        if (err?.name === "AbortError" || err?.name === "CanceledError") return;
        setBookingSearchStatus("idle");
      }
    },
    [dispatch]
  );

  const handleHide = useCallback(() => {
    resetForm();
    setBookingOptions([]);
    setBookingSearchStatus("idle");
    hide();
  }, [hide, resetForm]);

  return (
    <Modal show={show} onHide={handleHide} centered>
      <div className="modal-content">
        <div className="modal-header">
          <h1 className="modal-title fs-5">Use Credit</h1>
          <button
            type="button"
            className="btn-close"
            onClick={handleHide}
          ></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="row">
              <div className="col-xl-12 mb-3">
                <small className="text-muted">
                  Remaining: <strong>₹{remainingAmount.toFixed(2)}</strong>
                </small>
              </div>
              <div className="col-xl-12 mb-3">
                <CustomInputWithLabel
                  id="amount_used"
                  inputType="number"
                  labelText="Amount to Use"
                  required={true}
                  handleChange={handleChange}
                  value={values.amount_used}
                  touched={touched.amount_used}
                  errors={errors.amount_used}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <label className="form-label">Booking (optional)</label>
                <Select
                  isClearable
                  placeholder="Search booking by customer name..."
                  options={bookingOptions}
                  isLoading={bookingSearchStatus === "pending"}
                  onInputChange={handleBookingSearch}
                  onChange={(e) =>
                    setFieldValue("booking", e?.value || "")
                  }
                  value={
                    bookingOptions.find((o) => o.value === values.booking) ||
                    null
                  }
                  filterOption={() => true}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? bookingSearchStatus === "pending"
                        ? "Searching..."
                        : "No bookings found"
                      : "Type to search"
                  }
                />
              </div>
              <div className="col-xl-12">
                <CustomTextAreaWithLabel
                  id="notes"
                  labelText="Notes"
                  handleChange={handleChange}
                  value={values.notes}
                  touched={touched.notes}
                  errors={errors.notes}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger light"
              onClick={handleHide}
            >
              Close
            </button>
            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary"
            >
              {pending ? "Applying..." : "Apply Credit"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default UseCreditModal;
